import Phaser from 'phaser';
import { ToasterTruck } from '../sprites/ToasterTruck';
import { Toast } from '../sprites/Toast';
import { Enemy } from '../sprites/Enemy';
import { EnemyProjectile } from '../sprites/EnemyProjectile';
import { Barrier } from '../sprites/Barrier';
import { PowerUp } from '../sprites/PowerUp';
import { ScoreManager } from './ScoreManager';
import { WaveManager } from './WaveManager';
import { PowerUpManager } from './PowerUpManager';

export class CollisionManager {
  private scene: Phaser.Scene;
  private scoreManager: ScoreManager;
  private waveManager: WaveManager;
  private powerUpManager?: PowerUpManager;
  private onPlayerHit?: () => void;
  private onEnemyDestroyed?: (enemy: Enemy) => void;

  constructor(
    scene: Phaser.Scene,
    scoreManager: ScoreManager,
    waveManager: WaveManager
  ) {
    this.scene = scene;
    this.scoreManager = scoreManager;
    this.waveManager = waveManager;
  }

  setPowerUpManager(powerUpManager: PowerUpManager): void {
    this.powerUpManager = powerUpManager;
  }

  setup(
    truck: ToasterTruck,
    toastGroup: Phaser.Physics.Arcade.Group,
    enemyGroup: Phaser.Physics.Arcade.Group,
    enemyProjectileGroup: Phaser.Physics.Arcade.Group,
    barrierGroup?: Phaser.Physics.Arcade.Group,
    powerUpGroup?: Phaser.Physics.Arcade.Group
  ): void {
    // Toast hits enemy
    this.scene.physics.add.overlap(
      toastGroup,
      enemyGroup,
      this.handleToastHitEnemy.bind(this) as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this
    );

    // Enemy projectile hits player
    this.scene.physics.add.overlap(
      truck,
      enemyProjectileGroup,
      this.handleProjectileHitPlayer.bind(this) as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this
    );

    // Barrier collisions (if barriers exist)
    if (barrierGroup) {
      // Toast hits barrier
      this.scene.physics.add.overlap(
        toastGroup,
        barrierGroup,
        this.handleToastHitBarrier.bind(this) as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      );

      // Enemy projectile hits barrier
      this.scene.physics.add.overlap(
        enemyProjectileGroup,
        barrierGroup,
        this.handleProjectileHitBarrier.bind(this) as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      );

      // Enemy crashes into barrier
      this.scene.physics.add.overlap(
        enemyGroup,
        barrierGroup,
        this.handleEnemyHitBarrier.bind(this) as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      );
    }

    // Power-up collection
    if (powerUpGroup) {
      this.scene.physics.add.overlap(
        truck,
        powerUpGroup,
        this.handlePowerUpCollect.bind(this) as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      );
    }
  }

  private handlePowerUpCollect(
    _truck: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    powerUp: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const powerUpSprite = powerUp as PowerUp;
    
    if (!powerUpSprite.active) return;
    
    this.powerUpManager?.collectPowerUp(powerUpSprite);
  }

  private handleToastHitEnemy(
    toast: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    enemy: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const toastSprite = toast as Toast;
    const enemySprite = enemy as Enemy;

    if (!toastSprite.active || !enemySprite.active) return;

    toastSprite.destroy();

    const isDead = enemySprite.takeDamage(1);

    if (isDead) {
      this.scoreManager.addPoints(enemySprite.points);
      this.waveManager.onEnemyKilled();
      this.createDeathEffect(enemySprite.x, enemySprite.y, this.getEnemyColor(enemySprite));
      this.onEnemyDestroyed?.(enemySprite);
      enemySprite.destroy();
    }
  }

  private handleProjectileHitPlayer(
    _truck: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    projectile: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const projectileSprite = projectile as EnemyProjectile;
    
    if (!projectileSprite.active) return;

    projectileSprite.destroy();
    this.onPlayerHit?.();
  }

  private handleToastHitBarrier(
    toast: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    barrier: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const toastSprite = toast as Toast;
    const barrierSprite = barrier as Barrier;

    // Safety checks
    if (!toastSprite || !barrierSprite) return;
    if (!toastSprite.active || !barrierSprite.active) return;
    if (barrierSprite.getHealth() <= 0) return;

    // Calculate local hit position relative to barrier center
    const localX = toastSprite.x - barrierSprite.x;
    const localY = toastSprite.y - barrierSprite.y;

    toastSprite.destroy();
    barrierSprite.takeDamageAt(localX, localY);
  }

  private handleProjectileHitBarrier(
    projectile: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    barrier: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const projectileSprite = projectile as EnemyProjectile;
    const barrierSprite = barrier as Barrier;

    // Safety checks
    if (!projectileSprite || !barrierSprite) return;
    if (!projectileSprite.active || !barrierSprite.active) return;
    if (barrierSprite.getHealth() <= 0) return;

    // Calculate local hit position relative to barrier center
    const localX = projectileSprite.x - barrierSprite.x;
    const localY = projectileSprite.y - barrierSprite.y;

    projectileSprite.destroy();
    barrierSprite.takeDamageAt(localX, localY);
  }

  private handleEnemyHitBarrier(
    enemy: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile,
    barrier: Phaser.Types.Physics.Arcade.GameObjectWithBody | Phaser.Tilemaps.Tile
  ): void {
    const enemySprite = enemy as Enemy;
    const barrierSprite = barrier as Barrier;

    // Safety checks
    if (!enemySprite || !barrierSprite) return;
    if (!enemySprite.active || !barrierSprite.active) return;
    if (barrierSprite.getHealth() <= 0) return;

    // Calculate local hit position relative to barrier center
    const localX = enemySprite.x - barrierSprite.x;
    const localY = enemySprite.y - barrierSprite.y;

    // Barrier takes heavy damage from enemy crash
    barrierSprite.takeHeavyDamageAt(localX, localY);

    // Award points for the enemy
    this.scoreManager.addPoints(enemySprite.points);
    this.waveManager.onEnemyKilled();
    
    // Create death effect and destroy enemy
    this.createDeathEffect(enemySprite.x, enemySprite.y, this.getEnemyColor(enemySprite));
    this.onEnemyDestroyed?.(enemySprite);
    enemySprite.destroy();
  }

  private getEnemyColor(enemy: Enemy): number {
    // Return color based on enemy texture for death effect
    const texture = enemy.texture.key;
    if (texture === 'evilEgg' || texture === 'yolkProjectile') return 0xffff00;
    if (texture === 'angryBacon') return 0xff6644;
    if (texture === 'menacingCoffee') return 0x6a4a2a;
    if (texture.startsWith('alien_')) return 0x00ff00;
    return 0xffffff;
  }

  private createDeathEffect(x: number, y: number, color: number): void {
    // Central flash
    const flash = this.scene.add.circle(x, y, 15, 0xffffff, 1);
    this.scene.tweens.add({
      targets: flash,
      radius: 5,
      alpha: 0,
      duration: 100,
      onComplete: () => flash.destroy(),
    });

    // Create expanding ring effect
    const ring = this.scene.add.circle(x, y, 8, color, 0);
    ring.setStrokeStyle(3, color, 1);
    
    this.scene.tweens.add({
      targets: ring,
      radius: 40,
      alpha: 0,
      duration: 350,
      ease: 'Power2',
      onComplete: () => ring.destroy(),
    });

    // Secondary ring
    const ring2 = this.scene.add.circle(x, y, 5, color, 0);
    ring2.setStrokeStyle(2, 0xffffff, 0.8);
    
    this.scene.tweens.add({
      targets: ring2,
      radius: 25,
      alpha: 0,
      duration: 250,
      ease: 'Power2',
      onComplete: () => ring2.destroy(),
    });

    // Create scatter particles (more particles, varied sizes)
    const particleCount = 10;
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.3;
      const speed = 35 + Math.random() * 30;
      const size = 2 + Math.random() * 3;
      const particle = this.scene.add.circle(x, y, size, color, 1);
      
      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed,
        alpha: 0,
        scale: 0.3,
        duration: 300 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      });
    }

    // Spark trails
    for (let i = 0; i < 5; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 25 + Math.random() * 35;
      const spark = this.scene.add.rectangle(x, y, 2, 6, color, 1);
      spark.setRotation(angle);
      
      this.scene.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scaleY: 2,
        duration: 200 + Math.random() * 150,
        onComplete: () => spark.destroy(),
      });
    }

    // Score popup
    const enemy = this.scene.children.list.find(
      (child) => child instanceof Enemy && child.x === x && child.y === y
    ) as Enemy | undefined;
    
    if (enemy) {
      this.createScorePopup(x, y - 20, enemy.points);
    }
  }

  private createScorePopup(x: number, y: number, points: number): void {
    const text = this.scene.add.text(x, y, `+${points}`, {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#ffff00',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    this.scene.tweens.add({
      targets: text,
      y: y - 40,
      alpha: 0,
      scale: 1.3,
      duration: 600,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  setOnPlayerHit(callback: () => void): void {
    this.onPlayerHit = callback;
  }

  setOnEnemyDestroyed(callback: (enemy: Enemy) => void): void {
    this.onEnemyDestroyed = callback;
  }
}
